// Package imports:
import React, { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
// import './SigtiRange.scss';
// Component imports:
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import DataStatusToast from './DataStatusToast';
import DatePicker from '../common/DatePicker';
import AddBookmark from '../bookmarks/AddBookmark';
import PriceChangeRange from './PriceChangeRange';
import TickDataHighChart from './TickDataHighChart_series-init';
import Book from './Book';
import ByLevelBook from './ByLevelBook';
import TradesUntil from './TradesUntil';
import MyAlerts from './MyAlerts';
import MyBookmarks from './MyBookmarks';
import OrderHistory from './OrderHistory';
import TickDataRange from './TickDataRange';
import RelevantNews from './RelevantNews';
import SigtiRangeHistory from './history/SigtiRangeHistory';
// Service imports:
import { getUniqueTickId, turnDateToSimpleTime, turnRequestedToData } from '../../services/utils';
import { sortTickData, sortTickDataWithBook, sortTickOrBookData } from '../../services/sortUtils';
import { getCauseTypeOfAlert } from '../../services/alertUtils';
import { isUserLoggedIn } from '../../services/loginUtils';
// Action imports:
import { requestPublicOrders, requestPublicTrades, requestLastPrice, requestPreviousTradingDay, requestPublicOrdersByLevel } from '../../redux-store/actions/publicActions';
import { requestShareNews } from '../../redux-store/actions/newsActions';
import { requestPrivateOrdersAndTrades } from '../../redux-store/actions/privateActions';
import { requestAlerts } from '../../redux-store/actions/alertActions';
import { requestBookmarks } from '../../redux-store/actions/bookmarkActions';
import { setSelectedTick } from '../../redux-store/actions/dashboardActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { isPrivateDataError, TRADE_REPORT_STATUS_MAP } from '../../types/api/PrivateTypes';
import { AlertTickData } from '../../types/api/AlertTypes';
import { BY_LEVEL_SYMBOLS, isWithBookObject, TickData, WithBook } from '../../types/api/Types';

type SecurityType = 'share' | 'bond' | 'derivative' | 'etf';
type PanelTabs = 'Book/Trades' | 'Alerts/Bookmarks' | 'Order History' | 'Relevant News';

export type AlertDataIdToCauseMap = {
    [T in number]?: TickData
}

export type TickUniqueIdToAlertMap = {
    [T in string]?: AlertTickData
}

interface IPanelTabInfo {
    title: PanelTabs,
    isOnlyForShare: boolean
}
const PANEL_TABS: IPanelTabInfo[] = [{
    title: 'Book/Trades',
    isOnlyForShare: false
},{
    title: 'Alerts/Bookmarks',
    isOnlyForShare: false
},{
    title: 'Order History',
    isOnlyForShare: false
},{
    title: 'Relevant News',
    isOnlyForShare: true
}];
interface IOwnProps {
    symbol: string
};
type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps & IOwnProps;

const SigtiRange: React.FC<Props> = ({
    symbol,
    currentTradingDay,
    selectedTick,
    accessTokenV2,
    hasPrivateData,
    privateOrderAndTradeDoubleMap,
    publicOrderDoubleMap,
    publicOrderByLevelDoubleMap,
    publicTradeDoubleMap,
    alertsByDate,
    lastPriceDoubleMap,
    newsDoubleMap,
    hasEmsToken,
    autoComplete,
    bookmarks,
    requestPublicOrders,
    requestPublicOrdersByLevel,
    requestPublicTrades,
    requestLastPrice,
    requestPreviousTradingDay,
    requestPrivateOrdersAndTrades,
    requestShareNews,
    requestAlerts,
    requestBookmarks,
    setSelectedTick
}) => {
    useEffect(() => {
        document.title = `${symbol} - ${currentTradingDay} | KODIAK Sigti`;
    }, [symbol, currentTradingDay]);

    // Initialize state.
    const [ securityType, setSecurityType ] = useState<SecurityType | null>(null)
    const [ currentTab, setCurrentTab ] = useState<PanelTabs>(PANEL_TABS[0].title);
    const [ alertDataIdToCauseMap, setAlertDataIdToCauseMap ] = useState<AlertDataIdToCauseMap>({});
    const [ tickUniqueIdToAlertMap, setTickUniqueIdToAlertMap ] = useState<TickUniqueIdToAlertMap>({});
    const [ hasFilledAlertAndCauseMaps, setHasFilledAlertAndCauseMaps ] = useState(false);
    const [ viewTab, setViewTab] = useState<'Day view' | 'History view'>('Day view');

    // By order or by level
    const isSymbolByLevel = useMemo(() => {
        return BY_LEVEL_SYMBOLS.includes(symbol);
    }, [symbol]);


    // Security Type Effect.
    useEffect(() => {
        if (
            // Security type has not been set.
            securityType === null
            // And autocomplete is fetched successfully.
            && autoComplete !== undefined
            && autoComplete !== 'waiting'
            && !(autoComplete instanceof Error)
        ) {
            if ('bonds' in autoComplete) {
                const { bonds, shares, derivatives, etf } = autoComplete;
                // Bond.
                if (bonds.find(bond => bond.symbol === symbol)) {
                    setSecurityType('bond');
                }
                // Share.
                else if (shares.find(share => share.symbol === symbol)) {
                    setSecurityType('share');
                }
                // Derivative.
                else if (derivatives.find(der => der.symbol === symbol)) {
                    setSecurityType('derivative');
                }
                // ETF.
                else if (etf.find(fund => fund.symbol === symbol)) {
                    setSecurityType('etf');
                }
            }
        }
    }, [ securityType, autoComplete, symbol ])

    /* Fetch Data Effects. */
    // Public Orders.
    useEffect(() => {
        if (isSymbolByLevel) {
            if (publicOrderByLevelDoubleMap?.[symbol]?.[currentTradingDay] === undefined) {
                requestPublicOrdersByLevel(symbol, currentTradingDay, accessTokenV2)
            }
        } else {
            if (publicOrderDoubleMap?.[symbol]?.[currentTradingDay] === undefined) {
                requestPublicOrders(symbol, currentTradingDay, accessTokenV2)
            }
        }
    }, [publicOrderDoubleMap, symbol, isSymbolByLevel, currentTradingDay, accessTokenV2, requestPublicOrders, publicOrderByLevelDoubleMap, requestPublicOrdersByLevel]);

    // Public Trades.
    useEffect(() => {
        if (publicTradeDoubleMap?.[symbol]?.[currentTradingDay] === undefined) {
            requestPublicTrades(symbol, currentTradingDay, accessTokenV2)
        }
    }, [publicTradeDoubleMap, symbol, currentTradingDay, accessTokenV2, requestPublicTrades]);

    // Last Price And Change.
    useEffect(() => {
        const currentLastPriceDoubleMapEntry = lastPriceDoubleMap?.[symbol]?.[currentTradingDay];
        // Fetch last price if unfetched.
        if (currentLastPriceDoubleMapEntry === undefined) {
            requestLastPrice(symbol, currentTradingDay, accessTokenV2);
        } else {
            // If last price data has been fetched...
            if (
                currentLastPriceDoubleMapEntry !== 'waiting'
                && !(currentLastPriceDoubleMapEntry instanceof Error)
            ) {
                // ... start fetching lastTradingDay data.
                const lastTradingDay = 
                    currentLastPriceDoubleMapEntry && 
                    'previousTradingDay' in currentLastPriceDoubleMapEntry 
                        ? currentLastPriceDoubleMapEntry.previousTradingDay 
                        : undefined;
                // If unfetched, fetch.
                if (lastTradingDay === undefined) {
                    requestPreviousTradingDay(symbol, currentTradingDay, accessTokenV2);
                } else {
                     // If last trading day data is fetched, get lastPrice for last trading day.
                    if (
                        lastTradingDay !== 'waiting'
                        && !(lastTradingDay instanceof Error)
                        && lastTradingDay !== null
                    ) {
                        const prevTradingDayLastPriceDoubleMapEntry = 
                            typeof lastTradingDay === 'string' 
                                ? lastPriceDoubleMap?.[symbol]?.[lastTradingDay] 
                                : undefined;
                        if (prevTradingDayLastPriceDoubleMapEntry === undefined) {
                            if (typeof lastTradingDay === 'string') {
                                requestLastPrice(symbol, lastTradingDay, accessTokenV2);
                            }
                        }
                    }
                }
            }
        }
    }, [lastPriceDoubleMap, symbol, currentTradingDay, accessTokenV2, requestLastPrice, requestPreviousTradingDay]);
    
    // News.
    useEffect(() => {
        if (newsDoubleMap?.[symbol]?.[currentTradingDay] === undefined) {
            requestShareNews(symbol, currentTradingDay, accessTokenV2);
        }
    }, [newsDoubleMap, symbol, currentTradingDay, accessTokenV2, requestShareNews]);

    // Alerts.
    useEffect(() => {
        const alertInfo = alertsByDate[currentTradingDay];
        if (alertInfo === undefined) {
            requestAlerts(accessTokenV2, currentTradingDay, currentTradingDay);
        }
    }, [alertsByDate, currentTradingDay, accessTokenV2, requestAlerts]);

    // Bookmarks.
    useEffect(() => {
        if (bookmarks === null) {
            requestBookmarks(accessTokenV2);
        }
    }, [bookmarks, accessTokenV2, requestBookmarks]);

    // Private Orders And Trades.
    useEffect(() => {
        const privateOrderAndTradeInfo = privateOrderAndTradeDoubleMap?.[symbol]?.[currentTradingDay];
        if (hasPrivateData) {
            if (
                // Required for requesting.
                securityType !== null
                && accessTokenV2 !== null
                // Additionally, fetch the data if:
                && (
                    // 1. The data hasn't beeen fetched yet.
                    privateOrderAndTradeInfo === undefined
                    || (
                        // OR 2. There was an error fetching the data, but your current endpoint is different from the one that failed.
                        isPrivateDataError(privateOrderAndTradeInfo)
                    )
                )
            ) {
                const gateway = (securityType === 'bond') ? 'fusion' : 'inet';
                requestPrivateOrdersAndTrades(symbol, currentTradingDay, accessTokenV2, gateway);
            }
        }
    }, [privateOrderAndTradeDoubleMap, symbol, currentTradingDay, securityType, accessTokenV2, hasPrivateData, requestPrivateOrdersAndTrades]);

    /* Transform data into displayable data. */

    // Get public orders.
    const { publicOrdersData, publicOrders, sortedPublicOrderTickData } = useMemo(() => {
        const publicOrdersData = publicOrderDoubleMap?.[symbol]?.[currentTradingDay];
        const publicOrders = turnRequestedToData(publicOrdersData, []);
        const sortedPublicOrderTickData = sortTickDataWithBook(publicOrders);
        return { publicOrdersData, publicOrders, sortedPublicOrderTickData };
    }, [publicOrderDoubleMap, symbol, currentTradingDay]);

    // Get public orders with level.
    const { publicOrdersByLevelData, sortedPublicOrdersByFirstLevelTickData, sortedPublicOrdersByLevelTickData } = useMemo(() => {
        const publicOrdersByLevelData = publicOrderByLevelDoubleMap?.[symbol]?.[currentTradingDay];
        const publicOrdersByLevel = turnRequestedToData(publicOrdersByLevelData, []);
        const publicOrdersByFirstLevel = publicOrdersByLevel.filter(order => order.tick.level === 1);
        const sortedPublicOrdersByLevelTickData = sortTickDataWithBook(publicOrdersByLevel);
        const sortedPublicOrdersByFirstLevelTickData = sortTickDataWithBook(publicOrdersByFirstLevel);
        return { publicOrdersByLevelData, sortedPublicOrdersByLevelTickData, sortedPublicOrdersByFirstLevelTickData };
    }, [ publicOrderByLevelDoubleMap, symbol, currentTradingDay ]);

    // Get public trades.
    const { publicTradesData, sortedPublicTradeTickData } = useMemo(() => {

        // This only runs when loading fresh data (new Tick Data is fetched).
        if (publicTradeDoubleMap?.[symbol]?.[currentTradingDay] !== undefined && publicTradeDoubleMap?.[symbol]?.[currentTradingDay] !== "waiting") {
            setHasFilledAlertAndCauseMaps(false);
        }

        const publicTradesData = publicTradeDoubleMap?.[symbol]?.[currentTradingDay];
        const publicTrades = turnRequestedToData(publicTradesData, []);
        const sortedPublicTradeTickData = sortTickData(publicTrades)
        return { publicTradesData, sortedPublicTradeTickData };
    }, [ publicTradeDoubleMap, symbol, currentTradingDay ]);

    // Get last price and change.
    const { lastPrice, priceChange } = useMemo(() => {
        const lastPriceRequestedData = lastPriceDoubleMap?.[symbol]?.[currentTradingDay];
        const lastPriceData = turnRequestedToData(lastPriceRequestedData, null);
        const lastPrice = lastPriceData?.lastPrice || null;
        const lastPriceRequestedPreviousTradingDay = lastPriceData?.previousTradingDay;
        const previousTradingDay = turnRequestedToData(lastPriceRequestedPreviousTradingDay, null);
        const previousTradingDayLastPriceRequestedData = (previousTradingDay === null)
            ? undefined
            : lastPriceDoubleMap?.[symbol]?.[previousTradingDay];
        const previousTradingDayLastPriceData = turnRequestedToData(previousTradingDayLastPriceRequestedData, null);
        const previousLastPrice = previousTradingDayLastPriceData?.lastPrice || null;
        const priceChange = (lastPrice === null || previousLastPrice === null)
            ? null
            : ((lastPrice / previousLastPrice) - 1);
        return { lastPrice, priceChange };
    }, [ lastPriceDoubleMap, symbol, currentTradingDay ]);

    // Get alerts. 
    const { alertsData, sortedAlertTickData } = useMemo(() => {
        const alertsData = alertsByDate?.[currentTradingDay];
        const alerts = turnRequestedToData(alertsData, []).filter(alert => alert.symbol === symbol);
        const sortedAlertTickData = sortTickData(alerts);
        return { alertsData, sortedAlertTickData };
    }, [ alertsByDate, symbol, currentTradingDay ]);

    // Get news tickdata.
    const { newsData, sortedNewsTickData } = useMemo(() => {
        const newsData = newsDoubleMap?.[symbol]?.[currentTradingDay];
        const newsTickData = turnRequestedToData(newsData, []);
        const sortedNewsTickData = sortTickData(newsTickData);
        return { newsData, sortedNewsTickData };
    }, [ newsDoubleMap, symbol, currentTradingDay ]);

    // Get bookmark tickdata.
    const bookmarkTickData = useMemo(() => {
        if (bookmarks === null || bookmarks === 'waiting') return [];
        return bookmarks.filter(bookmark => (
            bookmark.symbol === symbol
            && turnDateToSimpleTime(new Date(bookmark.timestamp)) === currentTradingDay
        ));
    }, [ bookmarks, symbol, currentTradingDay ]);

    // Get private orders and trades.
    const {
        privateOrderAndTradeRequestedData,
        privateOrders,
        privateTrades,
        privateInetTradeReports,
        privateGeniumInetTradeReports
    } = useMemo(() => {
        const privateOrderAndTradeRequestedData = privateOrderAndTradeDoubleMap?.[symbol]?.[currentTradingDay];
        const privateOrderAndTradeData = turnRequestedToData(privateOrderAndTradeRequestedData, null);
        const privateOrders = privateOrderAndTradeData?.privateOrders ?? [];
        const privateTrades = privateOrderAndTradeData?.privateTrades ?? [];
        const privateInetTradeReports = privateOrderAndTradeData?.privateInetTradeReports ?? [];
        const privateGeniumInetTradeReports = privateOrderAndTradeData?.privateGeniumInetTradeReports ?? [];
        return {
            privateOrderAndTradeRequestedData,
            privateOrders,
            privateTrades,
            privateInetTradeReports,
            privateGeniumInetTradeReports
        };
    }, [ privateOrderAndTradeDoubleMap, symbol, currentTradingDay ]);
    
    /* Combine into TickData */

    const sortedTickData = useMemo(() => {
        const tickData: Array<TickData | WithBook<TickData>> = [
            ...sortedPublicOrderTickData,
            ...sortedPublicOrdersByFirstLevelTickData,
            ...sortedPublicTradeTickData,
            ...sortedNewsTickData,
            ...bookmarkTickData,
            ...privateOrders,
            ...privateTrades,
            ...privateInetTradeReports,
            ...privateGeniumInetTradeReports,
            ...sortedAlertTickData
        ];
        return sortTickOrBookData(tickData);
    }, [
        sortedPublicOrderTickData,
        sortedPublicOrdersByFirstLevelTickData,
        sortedPublicTradeTickData,
        sortedNewsTickData,
        bookmarkTickData,
        privateOrders,
        privateTrades,
        privateInetTradeReports,
        privateGeniumInetTradeReports,
        sortedAlertTickData
    ]);
    // Selected Tick effects:
    const isAllTickDataFetched = useMemo(() => {
        const isAllTickDataFetched = (
            (
                Array.isArray(publicOrdersData) || publicOrdersData instanceof Error ||
                Array.isArray(publicOrdersByLevelData) || publicOrdersByLevelData instanceof Error
            ) && (
                Array.isArray(publicTradesData) || publicTradesData instanceof Error
            ) &&
            (
                !hasEmsToken ||
                !(
                    privateOrderAndTradeRequestedData === undefined
                    || privateOrderAndTradeRequestedData === 'waiting'
            )
            ) && (
                Array.isArray(alertsData) || alertsData instanceof Error
            ) && (
                Array.isArray(newsData) || newsData instanceof Error
            ) &&
            Array.isArray(bookmarks)
        );
        return isAllTickDataFetched;
    }, [
        publicOrdersData,
        publicOrdersByLevelData,
        publicTradesData,
        privateOrderAndTradeRequestedData,
        alertsData,
        newsData,
        bookmarks,
        hasEmsToken
    ])

    // Scroll to tick when selected tick changes
    useEffect(() => {
        if (selectedTick) {
            const tickId = getUniqueTickId(selectedTick);
            if (tickId) {
                window.history.replaceState(true, '', `#${tickId}`);
                document.getElementById(tickId)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                // window.location.hash = tickId;
            }
        }
    }, [selectedTick]);

    // Set selectedTick on first render
    useEffect(() => {
        const { hash } = window.location;
        if (hash !== '' && isAllTickDataFetched) {
            // remove # from hash
            const correctedHash = hash.slice(1);
            const possiblySelectedTick = sortedTickData.find(tickOrWithBook => getUniqueTickId(isWithBookObject(tickOrWithBook) ? tickOrWithBook.tick : tickOrWithBook) === correctedHash);
            if (possiblySelectedTick) {
                if (isWithBookObject(possiblySelectedTick)) {
                    setSelectedTick(possiblySelectedTick.tick);
                } else {
                    setSelectedTick(possiblySelectedTick);
                }
            }
        }
        // eslint-disable-next-line
    }, [ isAllTickDataFetched, sortedTickData ]);

    useEffect(() => {
        if (!hasFilledAlertAndCauseMaps && isAllTickDataFetched) {
            const newAlertToCauseMap: AlertDataIdToCauseMap = {};
            const newTickUniqueIdToAlertMap: TickUniqueIdToAlertMap = {};

            for (const alert of sortedAlertTickData) {
                const alertId = alert.ad_id;

                if (alertId === null) continue;

                const causeType = getCauseTypeOfAlert(alert);

                // Order
                if (causeType === 'Order') {
                    const orderId = alert.order_id;
                    const refTimestamp = alert.ref_timestamp;
                    if (orderId === null || refTimestamp === null) continue;
                    const order = sortedPublicOrderTickData.find(order => (
                        String(order.tick.order_id) === String(orderId)
                        && order.tick.timestamp !== null
                        && refTimestamp !== null
                        && new Date(order.tick.timestamp).getTime() === new Date(refTimestamp).getTime()
                    ));
                    if (order) {
                        newAlertToCauseMap[alertId] = order.tick;
                        const uniqueTickId = getUniqueTickId(order.tick);
                        if (uniqueTickId) newTickUniqueIdToAlertMap[uniqueTickId] = alert;
                    }
                }
                // Trade
                else if (causeType === 'Trade') {
                    const tradeNumber = alert.trade_id;
                    if (tradeNumber === null) continue;
                    const trade = sortedPublicTradeTickData.find(trade => (
                        trade.trade_number.toString() === tradeNumber.toString()
                    ));
                    if (trade) {
                        newAlertToCauseMap[alertId] = trade;
                        const uniqueTickId = getUniqueTickId(trade);
                        if (uniqueTickId) newTickUniqueIdToAlertMap[uniqueTickId] = alert;
                    }
                }
                // Private Order
                else if (causeType === 'PrivateOrder') {
                    const privateOrderRef = alert.private_order_id;
                    if (privateOrderRef === null) {
                        // Deprecation check.
                        const orderId = alert.order_id;
                        const refTimestamp = alert.ref_timestamp;
                        if (orderId !== null && alert.ref_timestamp !== null) {
                            const possibleMatches = privateOrders.filter(privateOrder => (
                                privateOrder.market_feed_id === orderId.toString()
                                && privateOrder.timestamp === refTimestamp
                            ));
                            if (possibleMatches.length === 1) {
                                const privateOrder = possibleMatches[0];
                                newAlertToCauseMap[alertId] = privateOrder;
                                const uniqueTickId = getUniqueTickId(privateOrder);
                                if (uniqueTickId) newTickUniqueIdToAlertMap[uniqueTickId] = alert;
                            }
                        }
                        continue;
                    }
                    const privateOrderRefParts = privateOrderRef.split(';');
                    if (privateOrderRefParts.length !== 2) continue;
                    const execId = privateOrderRefParts[0];
                    const orderId = privateOrderRefParts[1];
                    const privateOrder = privateOrders.find(privateOrder => (
                        privateOrder.id === orderId
                        && privateOrder.exec_id === execId
                    ));
                    if (privateOrder) {
                        newAlertToCauseMap[alertId] = privateOrder;
                        const uniqueTickId = getUniqueTickId(privateOrder);
                        if (uniqueTickId) newTickUniqueIdToAlertMap[uniqueTickId] = alert;
                    }
                }
                // Private Trade
                else if (causeType === 'PrivateTrade') {
                    const privateTradeRef = alert.private_trade_id;
                    if (privateTradeRef === null) {
                        // Deprecation check.
                        const refTimestamp = alert.ref_timestamp;
                        if (refTimestamp !== null) {
                            const possibleMatches = privateTrades.filter(privateTrade => (
                                privateTrade.timestamp === refTimestamp
                            ));
                            if (possibleMatches.length === 1) {
                                const privateTrade = possibleMatches[0];
                                newAlertToCauseMap[alertId] = privateTrade;
                                const uniqueTickId = getUniqueTickId(privateTrade);
                                if (uniqueTickId) newTickUniqueIdToAlertMap[uniqueTickId] = alert;
                            }
                        }
                        continue;
                    }
                    const privateTradeRefParts = privateTradeRef.split(';');
                    if (privateTradeRefParts.length !== 3) continue;
                    const tradeId = privateTradeRefParts[0];
                    const orderId = privateTradeRefParts[1];
                    const execId = privateTradeRefParts[2];
                    const privateTrade = privateTrades.find(privateTrade => (
                        privateTrade.trade_id === tradeId
                        && privateTrade.order_id === orderId
                        && privateTrade.exec_id === execId
                    ));
                    if (privateTrade) {
                        newAlertToCauseMap[alertId] = privateTrade;
                        const uniqueTickId = getUniqueTickId(privateTrade);
                        if (uniqueTickId) newTickUniqueIdToAlertMap[uniqueTickId] = alert;
                    }
                }
                // Private Trade Report
                else if (causeType === 'PrivateTradeReport') {
                    const privateTradeReportRef = alert.private_trade_report_id;
                    if (privateTradeReportRef === null) {
                        // Deprecation check.
                        const refTimestamp = alert.ref_timestamp;
                        if (refTimestamp !== null) {
                            const possibleMatches = [...privateInetTradeReports, ...privateGeniumInetTradeReports].filter(privateTradeReport => (
                                privateTradeReport.timestamp === refTimestamp
                            ));
                            if (possibleMatches.length === 1) {
                                const privateTradeReport = possibleMatches[0];
                                newAlertToCauseMap[alertId] = privateTradeReport;
                                const uniqueTickId = getUniqueTickId(privateTradeReport);
                                if (uniqueTickId) newTickUniqueIdToAlertMap[uniqueTickId] = alert;
                            }
                        }
                        continue;
                    }
                    const privateTradeReportRefParts = privateTradeReportRef.split(';');
                    if (privateTradeReportRefParts.length !== 2) continue;
                    const id = privateTradeReportRefParts[0];
                    const status = privateTradeReportRefParts[1];
                    const updateCodeStatus = TRADE_REPORT_STATUS_MAP[status];
                    const privateTradeReport = [...privateInetTradeReports, ...privateGeniumInetTradeReports].find(privateTradeReport => (
                        privateTradeReport.id === id
                        && privateTradeReport.status === updateCodeStatus
                    ));
                    if (privateTradeReport) {
                        newAlertToCauseMap[alertId] = privateTradeReport;
                        const uniqueTickId = getUniqueTickId(privateTradeReport);
                        if (uniqueTickId) newTickUniqueIdToAlertMap[uniqueTickId] = alert;
                    }
                }
            }
            setHasFilledAlertAndCauseMaps(true);
            setAlertDataIdToCauseMap(newAlertToCauseMap);
            setTickUniqueIdToAlertMap(newTickUniqueIdToAlertMap);
        }
        // eslint-disable-next-line
    }, [ isAllTickDataFetched, hasFilledAlertAndCauseMaps, sortedPublicOrderTickData, sortedPublicTradeTickData, sortedAlertTickData, privateOrders, privateTrades, privateInetTradeReports, privateGeniumInetTradeReports ]);

    return (
        <Container id='SigtiRange' fluid>
            <Row className='SecondaryNavBar'>
                <Col xl={9} md={9}>
                    <div className='MainSecondaryElementsWrapper'>
                        <h5 className='TitleSymbol'>{symbol}</h5>
                        <AddBookmark
                            symbol={symbol}
                        />
                    </div>
                </Col>
                <Col
                    xl={3} md={3}
                    className='PriceAndChangeCol'
                >
                    <PriceChangeRange
                        lastPrice={lastPrice}
                        priceChangePercent={priceChange}
                    />
                </Col>
            </Row>
            <Row className='PanelTabSelector'>
                <Col md={12}>
                <div className='BackgroundBar' />
                <div className='PanelTabs'>
                    {
                        ['Day view', 'History view']
                        .map((tab) => (
                            <div
                                key={tab}
                                onClick={() => setViewTab(tab as 'Day view' | 'History view')}
                                className={'PanelTab' + ((viewTab === tab) ? ' SelectedTab' : '')}
                            >
                                {
                                    (viewTab === tab)
                                    ? <div className='SelectedTabBackground' />
                                    : null
                                }
                                {tab}
                            </div>
                        ))
                    }
                </div>
                </Col>
            </Row>
            {viewTab === 'Day view' ? (
                <>
                <Row>
                <div className='SigtiRangeDatePickerWrapper'>
                    <DatePicker/>
                </div>
                <Col md={12}>
                    <TickDataHighChart
                        symbol={symbol}
                        sortedPublicOrderTickData={sortedPublicOrderTickData}
                        sortedPublicOrdersByFirstLevelTickData={sortedPublicOrdersByFirstLevelTickData}
                        sortedPublicTradeTickData={sortedPublicTradeTickData}
                        sortedAlertTickData={sortedAlertTickData}
                        sortedNewsTickData={sortedNewsTickData}
                        lastPrice={lastPrice}
                    />
                </Col>
                </Row>
                {/* Symble Information */}
                <Row className='TickDataRangeRow'>
                    <Col
                        xl={8}
                        md={12}
                        xs={12}
                        className='TickDataRangeCol'
                    >
                        <TickDataRange
                            securityType={securityType}
                            sortedTickData={sortedTickData}
                            newsDoubleMap={newsDoubleMap}
                            isSymbolByLevel={isSymbolByLevel}
                            alertDataIdToCauseMap={alertDataIdToCauseMap}
                            tickUniqueIdToAlertMap={tickUniqueIdToAlertMap}
                        />
                    </Col>
                    <Col
                        xl={4}
                        md={12}
                        xs={12}
                        className='PanelTabWrapperCol'
                    >
                        <div className='PanelTabSelector'>
                            <div className='BackgroundBar' />
                            <div className='PanelTabs'>
                                {
                                    PANEL_TABS
                                    // Some tabs are only visible depending on security type.
                                    .filter(({ isOnlyForShare }) => (
                                        !isOnlyForShare
                                        || (
                                            securityType === 'share'
                                            && isOnlyForShare
                                        )
                                    ))
                                    // Turn array into JSX.
                                    .map(({ title }) => (
                                        <div
                                            key={title}
                                            onClick={() => setCurrentTab(title)}
                                            className={'PanelTab' + ((currentTab === title) ? ' SelectedTab' : '')}
                                        >
                                            {
                                                (currentTab === title)
                                                ? <div className='SelectedTabBackground' />
                                                : null
                                            }
                                            {title}
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                        <div className='PanelTabWrapper'>
                            {
                                (currentTab === 'Book/Trades') // if
                                ? (
                                    <>
                                        {(isSymbolByLevel)
                                        ? (
                                            <ByLevelBook
                                                sortedPublicOrdersByLevelTickData={sortedPublicOrdersByLevelTickData}
                                            />
                                        ): (
                                            <Book
                                                sortedPublicOrderTickData={sortedPublicOrderTickData}
                                            />
                                        )}
                                        <TradesUntil
                                            symbol={symbol}
                                            sortedPublicTradeTickData={sortedPublicTradeTickData}
                                        />
                                    </>
                                ) 
                                : (currentTab === 'Alerts/Bookmarks') // else if 
                                ? (
                                    <>
                                        <MyAlerts
                                            alertDataIdToCauseMap={alertDataIdToCauseMap}
                                            symbol={symbol}
                                        />
                                        <MyBookmarks
                                            symbol={symbol}
                                        />
                                    </>
                                ) : (currentTab === 'Order History') // else if
                                ? (
                                    <OrderHistory
                                        publicOrders={publicOrders}
                                        privateOrders={privateOrders}
                                        privateTrades={privateTrades}
                                        symbol={symbol}
                                    />
                                ) : (
                                    <RelevantNews
                                        symbol={symbol}
                                    />
                                )
                            }
                        </div>
                        {/* Loading popup indicator */}
                        <DataStatusToast symbol={symbol} />
                    </Col>
                </Row>
                </>
                ): (
                    <SigtiRangeHistory symbol={symbol}/>
                )}
        </Container>
    );
}

function mapStateToProps({ dashboardReducer, loginReducer, privateReducer, publicReducer, alertReducer, newsReducer, bookmarkReducer }: RootState) {
    const { privateOrderAndTradeDoubleMap } = privateReducer;
    const { publicOrderDoubleMap, publicTradeDoubleMap, lastPriceDoubleMap, publicOrderByLevelDoubleMap } = publicReducer;
    const { newsDoubleMap } = newsReducer;
    const { currentTradingDay, autoComplete, selectedTick } = dashboardReducer;
    const { accessTokenV2, hasPrivateData } = loginReducer;
    const { hasEmsToken } = isUserLoggedIn(loginReducer);
    const { alertsByDate } = alertReducer
    const { bookmarks } = bookmarkReducer;
    return {
        currentTradingDay,
        selectedTick,
        accessTokenV2,
        hasPrivateData,
        privateOrderAndTradeDoubleMap,
        publicOrderDoubleMap,
        publicOrderByLevelDoubleMap,
        publicTradeDoubleMap,
        alertsByDate,
        lastPriceDoubleMap,
        newsDoubleMap,
        hasEmsToken,
        autoComplete,
        bookmarks
    };
}

const connector = connect(mapStateToProps, {
    requestPublicOrders,
    requestPublicOrdersByLevel,
    requestPublicTrades,
    requestLastPrice,
    requestPreviousTradingDay,
    requestPrivateOrdersAndTrades,
    requestShareNews,
    requestAlerts,
    requestBookmarks,
    setSelectedTick
});
export default connector(SigtiRange);